import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

function CandidatesTable() {
  const { electionId: paramElectionId, officeCode: paramOfficeCode } = useParams();
  const navigate = useNavigate();

  // State for options (fetched for elections, dynamic for offices)
  const [electionOptions, setElectionOptions] = useState([]);  // Fetched from API
  const [officeOptions, setOfficeOptions] = useState([]); // Will be derived from candidates for selected election

  // Local state for dropdowns (sync with URL params)
  const [selectedElection, setSelectedElection] = useState(paramElectionId || '');
  const [selectedOffice, setSelectedOffice] = useState(paramOfficeCode || 'GOV');
  const [data, setData] = useState([]);
  const [balances, setBalances] = useState([]); // New state for balances
  const [solicitationAccounts, setSolicitationAccounts] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingElections, setLoadingElections] = useState(true);
  const [electionError, setElectionError] = useState(null);
  const [error, setError] = useState(null);  // Table error state (moved up for scope)
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch election options from API
  useEffect(() => {
    const fetchElections = async () => {
      try {
        setLoadingElections(true);
        setElectionError(null);
        const response = await fetch('/api/electioncodes');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const allCodes = await response.json();
        
        // Filter for general elections (name ends with "Election")
        const filteredElections = allCodes
          .filter(code => code.name && code.name.endsWith('Election'))
          .map(code => ({ value: code.value, label: code.name }))
          .sort((a, b) => new Date(b.value) - new Date(a.value));  // Sort descending by date (newest first)
        
        setElectionOptions(filteredElections);
        // Prefer a 2026 election code if present, otherwise default to newest
        if (filteredElections.length > 0 && !paramElectionId) {
          const found2026 = filteredElections.find(fe => String(fe.value).includes('2026'));
          setSelectedElection(found2026 ? found2026.value : filteredElections[0].value);
        }
      } catch (err) {
        console.error('Fetch elections error:', err);
        setElectionError(err.message);
        // Fallback to hardcoded
        setElectionOptions([
          { value: '20181106-GEN', label: '2018 General Election' },
          { value: '20261103-GEN', label: '2026 General Election' }
        ]);
        if (!paramElectionId) {
          setSelectedElection('20261103-GEN');
        }
      } finally {
        setLoadingElections(false);
      }
    };

    fetchElections();
  }, [paramElectionId]);  // Re-run when the URL param changes

  // Sync dropdowns with URL params (after fetch)
  useEffect(() => {
    if (paramElectionId && electionOptions.find(opt => opt.value === paramElectionId)) {
      setSelectedElection(paramElectionId);
    }
    if (paramOfficeCode && officeOptions.find(opt => opt.value === paramOfficeCode)) {
      setSelectedOffice(paramOfficeCode);
    }
  }, [paramElectionId, paramOfficeCode, electionOptions, officeOptions]);  // Added officeOptions

  // When selectedElection changes, derive unique office options from the candidates for that election
  useEffect(() => {
    const fetchOfficesForElection = async () => {
      if (!selectedElection) return;
      try {
        const res = await fetch(`/api/candidates/election/${encodeURIComponent(selectedElection)}`);
        if (!res.ok) {
          console.warn('Failed to fetch candidates for election to derive offices:', res.status);
          setOfficeOptions([]);
          return;
        }
        const candidates = await res.json();
        const officeMap = new Map();
        (candidates || []).forEach(c => {
          const code = c.OfficeCode || c.Office || '';
          const desc = c.OfficeDesc || c.OfficeDescription || c.Office || code;
          if (code) {
            if (!officeMap.has(code)) officeMap.set(code, desc || code);
          }
        });
        const opts = Array.from(officeMap.entries()).map(([value, label]) => ({ value, label }));
        opts.sort((a, b) => a.label.localeCompare(b.label));
        setOfficeOptions(opts);
        if (!opts.find(o => o.value === selectedOffice) && opts.length > 0) {
          setSelectedOffice(opts[0].value);
        }
      } catch (err) {
        console.error('Error deriving office options:', err);
        setOfficeOptions([]);
      }
    };

    fetchOfficesForElection();
  }, [selectedElection]);

  // Fetch data on election/office change
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedElection || !selectedOffice) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch candidates first
        const candidatesResponse = await fetch(`/api/candidates/${selectedElection}/${selectedOffice}`);
        if (!candidatesResponse.ok) {
          throw new Error(`HTTP error for candidates! status: ${candidatesResponse.status}`);
        }
        const candidatesResult = await candidatesResponse.json();
        console.log('Fetched candidates:', candidatesResult); // Debug: Log candidates data
        console.log('Candidate AcctNums:', candidatesResult.map(c => ({ AcctNum: c.AcctNum, type: typeof c.AcctNum }))); // Debug: Log AcctNums and types
        setData(candidatesResult);

        // Extract AcctNums and also include any accountNums found in candidates' AccountList
        const acctNumsFromMain = candidatesResult.map(c => c.AcctNum).filter(num => num != null).map(String);
        const acctNumsFromAccountList = [];
        candidatesResult.forEach(c => {
          if (Array.isArray(c.AccountList)) {
            c.AccountList.forEach(a => {
              if (a && a.accountNum != null) acctNumsFromAccountList.push(String(a.accountNum));
            });
          }
        });

        // Merge and dedupe
        const allAcctNums = Array.from(new Set([...acctNumsFromMain, ...acctNumsFromAccountList]));

        if (allAcctNums.length > 0) {
          const balancesResponse = await fetch(`/api/balances?accounts=${allAcctNums.join(',')}`);
          if (balancesResponse.ok) {
            const balancesResult = await balancesResponse.json();
            console.log('Fetched balances (all acctnums):', balancesResult);
            setBalances(balancesResult);
          } else {
            console.error(`HTTP error for balances! status: ${balancesResponse.status}`);
            setBalances([]);
          }
        } else {
          setBalances([]);
        }

        // Fetch Statement of Solicitation documents and record the Account numbers that have them
        try {
          const docsRes = await fetch('/api/documents/type/' + encodeURIComponent('Statement of Solicitation'));
          if (docsRes.ok) {
            const docs = await docsRes.json();
            const acctSet = new Set(docs.map(d => String(d.Account).trim()));
            setSolicitationAccounts(acctSet);
          }
        } catch (err) {
          console.warn('Could not fetch solicitation documents', err);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedElection, selectedOffice, paramElectionId]);  // Added paramElectionId to deps for ESLint (though conditional)

  // Handle dropdown changes: Update URL and state
  const handleElectionChange = (e) => {
    const newElection = e.target.value;
    setSelectedElection(newElection);
    navigate(`/candidates/${newElection}/${selectedOffice}`);
  };

  const handleOfficeChange = (e) => {
    const newOffice = e.target.value;
    setSelectedOffice(newOffice);
    navigate(`/candidates/${selectedElection}/${newOffice}`);
  };

  // Memoized balances map for quick lookup by account (AcctNum)
  const balancesMap = useMemo(() => {
    // Be tolerant of different field names in balance documents (account, AcctNum, Account)
    const map = new Map(balances.map(balance => {
      const acct = balance.account ?? balance.AcctNum ?? balance.Account ?? balance.accountNum;
      return [String(acct).trim(), balance.coh];
    }));
    console.log('Balances Map keys:', Array.from(map.keys())); // Debug: Log map keys
    return map;
  }, [balances]);

  // (No lazy-load helper; balances fetched for all relevant accounts on initial load)

  // Memoized computed fields & sorting (by Juris1num ASC)
  const processedData = useMemo(() => {
    const normalizeAccountList = (rawList, fallbackAcct) => {
      if (!Array.isArray(rawList)) return fallbackAcct ? [String(fallbackAcct).trim()] : [];
      const normalized = rawList.map(a => {
        if (!a) return null;
        if (a.accountNum !== undefined && a.accountNum !== null) return String(a.accountNum).trim();
        // handle malformed object like { 'Campaign Account': 12345 }
        const entries = Object.entries(a).filter(([k]) => k !== '_id');
        if (entries.length === 1) return String(entries[0][1]).trim();
        return null;
      }).filter(Boolean);
      return normalized.length > 0 ? normalized : (fallbackAcct ? [String(fallbackAcct).trim()] : []);
    };

    const mappedData = data.map(row => {
      const acctNumsToSum = normalizeAccountList(row.AccountList, row.AcctNum);

      // Sum coh values from balancesMap for all relevant accounts
      let sumCoh = 0;
      let foundAny = false;
      acctNumsToSum.forEach(acct => {
        const v = balancesMap.get(String(acct).trim());
        if (v != null && !isNaN(Number(v))) {
          sumCoh += Number(v);
          foundAny = true;
        }
      });

      const cohValue = foundAny ? sumCoh : null;

      return {
        ...row,
        __normalizedAccountList: acctNumsToSum, // expose normalized list for rendering/expansion decisions
        fullName: `${row.NameFirst || ''} ${row.NameMiddle ? row.NameMiddle + ' ' : ''}${row.NameLast || ''}`.trim(),
        fullAddress: `${row.Addr1 || ''}${row.Addr2 ? ', ' + row.Addr2 : ''}, ${row.City || ''}, ${row.State || ''} ${row.Zip || ''}`.trim(),
        coh: cohValue // Sum of coh across AccountList or null
      };
    });

    return mappedData.sort((a, b) => {
      const aJuris = a.Juris1num || '';
      const bJuris = b.Juris1num || '';
      return aJuris.localeCompare(bJuris, undefined, { numeric: true });
    });
  }, [data, balancesMap]);

  // Column widths for better sizing
  const columnWidths = {
    account: '80px',
    name: '200px',
    party: '60px',
    office: '60px',
    district: '60px',
    status: '60px',
    coh: '120px'
  };

  if (loading) return <div>Loading election data...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      {/* <h2>Florida Election Candidates</h2> */}
      
      {/* Dropdowns for Selection */}
      <div style={{ marginBottom: '20px', padding: '10px', border: '1px solid #ddd' }}>
        <label style={{ marginRight: '10px' }}>Election ID: </label>
        {loadingElections ? (
          <select disabled style={{ marginRight: '20px' }}>
            <option>Loading elections...</option>
          </select>
        ) : electionError ? (
          <span style={{ color: 'red', marginRight: '20px' }}>Error loading elections: {electionError}</span>
        ) : (
          <select value={selectedElection} onChange={handleElectionChange} style={{ marginRight: '20px' }}>
            {electionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        )}
        
        <label style={{ marginRight: '10px' }}>Office: </label>
        <select value={selectedOffice} onChange={handleOfficeChange}>
          {officeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>

      {/* Wrapper for table to center and constrain width */}
      <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
        <table 
          border="1" 
          style={{ 
            borderCollapse: 'collapse', 
            tableLayout: 'fixed', // Fixed layout for consistent column widths
            width: 'auto', // Auto width based on column sums
            // minWidth: '910px', // Minimum to fit all columns
            margin: '10px auto' 
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f2f2f2' }}>
              <th style={{ width: columnWidths.account, padding: '8px' }}>Account #</th>
              <th style={{ width: columnWidths.name, padding: '8px' }}>Full Name</th>
              <th style={{ width: columnWidths.party, padding: '8px' }}>Party</th>
              <th style={{ width: columnWidths.office, padding: '8px' }}>Office</th>
              <th style={{ width: columnWidths.district, padding: '8px' }}>District</th>
              <th style={{ width: columnWidths.status, padding: '8px' }}>Status</th>
              <th style={{ width: columnWidths.coh, padding: '8px' }}>Cash On Hand</th>
            </tr>
          </thead>
          <tbody>
            {processedData.length > 0 ? (
              processedData.map((row, index) => {
                const jurisNum = parseInt(row.Juris1num, 10);
                const isElectionYear = selectedOffice === 'STS' && !isNaN(jurisNum) && (jurisNum - 1) % 4 < 2;
                const isOdd = selectedOffice !== 'STS' && !isNaN(jurisNum) && jurisNum % 2 === 1;
                const rowStyle = { backgroundColor: (isElectionYear || isOdd) ? '#e6f3ff' : 'transparent' };

                const idKey = row._id || row.AcctNum || index;
                const isExpanded = expandedRows.has(String(idKey));

                const toggleRow = () => {
                  const next = new Set(expandedRows);
                  const key = String(idKey);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  setExpandedRows(next);
                };

                return (
                  <React.Fragment key={idKey}>
                    <tr style={rowStyle}>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {Array.isArray(row.AccountList) && row.AccountList.length > 1 ? (
                          <a onClick={toggleRow} style={{ cursor: 'pointer', marginRight: 8, color: '#444' }}>{isExpanded ? '▾' : '▸'}</a>
                        ) : (
                          <span style={{ display: 'inline-block', width: 14, marginRight: 8 }} />
                        )}
                        <Link to={`/candidates/${row.AcctNum}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                          {solicitationAccounts.has(String(row.AcctNum)) ? <strong>{row.AcctNum}</strong> : row.AcctNum}
                        </Link>
                      </td>
                      <td style={{ padding: '4px' }}>{row.fullName}</td>
                      <td style={{ padding: '4px' }}>{row.PartyCode}</td>
                      <td style={{ padding: '4px' }}>{row.OfficeCode}</td>
                      <td style={{ padding: '4px' }}>{row.Juris1num}</td>
                      <td style={{ padding: '4px' }}>{row.StatusDesc}</td>
                      <td style={{ padding: '4px' }}>
                        {row.coh !== null 
                          ? `$${Number(row.coh).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                          : ''
                        }
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan="7" style={{ padding: 8, background: '#fafafa', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                          <div style={{ display: 'flex', gap: 16 }}>
                            <div style={{ flex: 1 }}>
                              <strong>Account breakdown</strong>
                              <div style={{ marginTop: 8 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ textAlign: 'left', color: '#333' }}>
                                      <th style={{ width: 140, padding: 6 }}>Account</th>
                                      <th style={{ padding: 6 }}>Name</th>
                                      <th style={{ width: 160, padding: 6, textAlign: 'right' }}>COH</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Array.isArray(row.AccountList) && row.AccountList.length > 0 ? (
                                      row.AccountList.map((a, i) => {
                                        const acctKey = String(a.accountNum).trim();
                                        const indiv = balancesMap.get(acctKey);
                                        return (
                                          <tr key={i}>
                                            <td style={{ padding: 6 }}>{a.accountNum}</td>
                                            <td style={{ padding: 6 }}>{a.accountName}</td>
                                            <td style={{ padding: 6, textAlign: 'right' }}>{indiv != null ? `$${Number(indiv).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</td>
                                          </tr>
                                        );
                                      })
                                    ) : (
                                      // Fallback: show the candidate's AcctNum
                                      <tr>
                                        <td style={{ padding: 6 }}>{row.AcctNum}</td>
                                        <td style={{ padding: 6 }}>Campaign</td>
                                        <td style={{ padding: 6, textAlign: 'right' }}>{balancesMap.get(String(row.AcctNum).trim()) != null ? `$${Number(balancesMap.get(String(row.AcctNum).trim())).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" style={{ padding: '8px', textAlign: 'center' }}>No candidates found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CandidatesTable;